import Bugsnag from '@bugsnag/js'
import { GraphQLFormattedError } from 'graphql'
import { useDispatch, useSelector } from 'react-redux'
import { appPath } from '../config'
import {
  SUPER_ENABLED_KEY,
  FROM_MOBILE_APP_KEY,
  getItemSafe,
  ASK_STANDALONE_ENABLED_KEY,
} from '../helpers/localStorage'
import { getReferralToken } from '../helpers/referralToken'
import {
  Analytics,
  CreateOrganizationInput,
  CreateOrganizationWithOauthCacheKeyInput,
  useCreateOrganizationMutation,
  useCreateOrganizationWithOauthCacheKeyInputMutation,
} from '../remote/graphql'
import { CreateSteps } from '../screens/Organization/Create/CreateSteps'
import {
  getEmailVerificationCode,
  getOrganization,
  getUser,
  setAppPath,
  setCreateOrgError,
  setStep,
} from '../screens/Organization/Create/state'
import {
  getDocCacheKeyFromUrl,
  getOrgCreationFlow,
  getStructureCacheKeyFromUrl,
} from '../utils'
import { getAnalyticsRawContext } from './useAnalytics'
import { getSuperTrialToken } from '../helpers/superTrial'

export const useCreateOrganization = ({
  loginOrigin,
}: {
  loginOrigin?: string
} = {}) => {
  const dispatch = useDispatch()
  const user = useSelector(getUser)
  const organization = useSelector(getOrganization)
  const emailVerificationCode = useSelector(getEmailVerificationCode)
  const fromMobileApp = getItemSafe(FROM_MOBILE_APP_KEY)
  const superEnabledValue =
    getItemSafe(SUPER_ENABLED_KEY) ?? getItemSafe(ASK_STANDALONE_ENABLED_KEY)
  const referralToken = getReferralToken()
  const superTrialToken = getSuperTrialToken()
  const utms = getAnalyticsRawContext()
  const orgCreationFlow = getOrgCreationFlow(appPath)
  const structureCacheKey = getStructureCacheKeyFromUrl()
  const docCacheKey = getDocCacheKeyFromUrl()

  const superEnabled = superEnabledValue === 'true'
  const { displayName, password, email, referrer, firstAttribution, cacheKey } =
    user

  const isOauthFlow = Boolean(cacheKey)

  const { marketingAttribution, remoteStance } = organization

  const analytics: Analytics = {
    referrer,
    firstAttribution,
    utms: {
      campaign: utms.utm_campaign,
      source: utms.utm_source,
      medium: utms.utm_medium,
      term: utms.utm_term,
      content: utms.utm_content,
    },
    gclid: utms.gclid,
    ga_clientid: utms.ga_clientid,
    fbclid: utms.fbclid,
    slt_device: utms.slt_device,

    orgCreationFlow,
    marketingAttribution,
    remoteStance,

    browserStats: {
      htmlScripts: document.querySelectorAll('html > script:not([src])').length,
    },
  }

  const errorHandler =
    (
      input: CreateOrganizationInput | CreateOrganizationWithOauthCacheKeyInput
    ) =>
    ({
      graphQLErrors,
    }: {
      graphQLErrors: readonly GraphQLFormattedError[]
    }) => {
      const error = graphQLErrors[0]
      Bugsnag.notify(new Error(error.message), (event) =>
        event.addMetadata('details', input)
      )
      dispatch(setCreateOrgError(error))
      dispatch(setStep(CreateSteps.Finished))
    }

  const createOrganizationInput = {
    userData: { email, password, displayName, emailVerificationCode },
    analytics,
    organizationData: undefined,
    returnTo: appPath,
    fromMobileApp: fromMobileApp === 'true',
    referralToken,
    structureCacheKey,
    docCacheKey,
    superEnabled,
    superTrialToken,
  }
  const createOrganizationMutation = useCreateOrganizationMutation({
    variables: {
      input: createOrganizationInput,
    },
    onCompleted: async (data) => {
      const url = data.createOrganization.url
      dispatch(setAppPath(url))
      dispatch(setStep(CreateSteps.Finished))
    },
    onError: errorHandler(createOrganizationInput),
  })

  const createOrganizationWithOauthCacheKeyInput = {
    cacheKey,
    analytics,
    organizationData: undefined,
    fromMobileApp: fromMobileApp === 'true',
    fromDesktopApp: loginOrigin === 'desktop',
    fromAskXDesktopApp: loginOrigin === 'askxdesktop',
    referralToken,
    superEnabled,
    superTrialToken,
  }
  const createOrganizationWithOauthCacheKeyInputMutation =
    useCreateOrganizationWithOauthCacheKeyInputMutation({
      variables: {
        input: createOrganizationWithOauthCacheKeyInput,
      },
      onCompleted: async (data) => {
        const url = data.createOrganizationWithOauthCacheKey.url
        dispatch(setAppPath(url))
        dispatch(setStep(CreateSteps.Finished))
      },
      onError: errorHandler(createOrganizationInput),
    })

  const createOrganization = isOauthFlow
    ? createOrganizationWithOauthCacheKeyInputMutation
    : createOrganizationMutation

  return createOrganization
}
